"use client";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  confirm,
  Input,
  Label,
  Loader,
  toast,
} from "@devhop/ui";
import { useParams, useSearch } from "@tanstack/react-router";
import { LayoutGrid, List, MapPin, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { WarehouseLayoutEditor } from "@/modules/merchant/presentation/ui/WarehouseLayoutEditor";
import { WarehouseLayoutFloorBadges } from "@/modules/merchant/presentation/ui/WarehouseLayoutFloorBadges";
import { WarehouseLayoutFloorsPanel } from "@/modules/merchant/presentation/ui/WarehouseLayoutFloorsPanel";
import { WarehouseLocationQrPrintButton } from "@/modules/merchant/presentation/ui/WarehouseLocationQrPrint";
import { WarehouseLocationScanModal } from "@/modules/merchant/presentation/ui/WarehouseLocationScanModal";
import { WarehouseZoneFloorSelect } from "@/modules/merchant/presentation/ui/WarehouseZoneFloorSelect";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useAssignWarehouseZoneFloorMutation,
  useCreateWarehouseLayoutFloorMutation,
  useCreateWarehouseLocationMutation,
  useCreateWarehouseZoneMutation,
  useDeleteWarehouseLayoutMutation,
  useDeleteWarehouseLocationMutation,
  useDeleteWarehouseZoneMutation,
  useShopBranchesListQuery,
  useUpdateWarehouseLocationMutation,
  useUpdateWarehouseZoneMutation,
  useWarehouseLayoutFloorsQuery,
  useWarehouseLayoutPlacementsQuery,
  useWarehouseLayoutQuery,
  useWarehouseLocationsQuery,
  useWarehouseZonesQuery,
} from "../api/queries";

type PageTab = "list" | "layout";

const ROUTE = "/app/shops/$shopId/inventory/locations" as const;

export function ShopWarehouseLocationsPage() {
  const params = useParams({ strict: false });
  const search = useSearch({ strict: false }) as {
    tab?: "list" | "layout";
    branchId?: string;
    layoutId?: string;
    focusLocationId?: string;
  };
  const shopId = params?.shopId as string | undefined;
  const [branchId, setBranchId] = useState<string>(search.branchId ?? "");
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | undefined>(
    search.layoutId,
  );
  const [zoneCode, setZoneCode] = useState("MAIN");
  const [zoneName, setZoneName] = useState("ໂຊນຫຼັກ");
  const [zoneFloorLayoutId, setZoneFloorLayoutId] = useState<string>("");
  const [selectedZoneId, setSelectedZoneId] = useState<string>("");
  const [editingZoneId, setEditingZoneId] = useState<string | null>(null);
  const [editZoneCode, setEditZoneCode] = useState("");
  const [editZoneName, setEditZoneName] = useState("");
  const [editingLocationId, setEditingLocationId] = useState<string | null>(
    null,
  );
  const [editLocCode, setEditLocCode] = useState("");
  const [editLocName, setEditLocName] = useState("");
  const [editLocIsDefaultReceive, setEditLocIsDefaultReceive] = useState(false);
  const [locCode, setLocCode] = useState("");
  const [locName, setLocName] = useState("");
  const [scanLookup, setScanLookup] = useState("");
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanModalInitial, setScanModalInitial] = useState<
    string | undefined
  >();
  const [pageTab, setPageTab] = useState<PageTab>(
    search.tab === "layout" ? "layout" : "list",
  );

  const { currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const warehouseLayoutOn = hasShopFeature(
    currentShop?.featureFlags,
    "warehouse_layout",
  );
  const activeTab: PageTab = warehouseLayoutOn ? pageTab : "list";

  useEffect(() => {
    if (search.branchId) setBranchId(search.branchId);
  }, [search.branchId]);

  useEffect(() => {
    if (search.layoutId) setSelectedLayoutId(search.layoutId);
  }, [search.layoutId]);

  useEffect(() => {
    if (search.tab === "layout" && warehouseLayoutOn) {
      setPageTab("layout");
    } else if (search.tab === "list") {
      setPageTab("list");
    }
  }, [search.tab, warehouseLayoutOn]);
  const canAdjust = canUseMerchantPermission(
    currentShop?.permissions ?? [],
    MerchantPermissions.inventory.adjust,
    verifiedLevel,
  );
  const branchesQuery = useShopBranchesListQuery(shopId);
  const branches = branchesQuery.data ?? [];

  const zonesQuery = useWarehouseZonesQuery(shopId, branchId || undefined);
  const zones = zonesQuery.data?.items ?? [];
  const activeZoneId = useMemo(() => {
    if (selectedZoneId && zones.some((z) => z.id === selectedZoneId)) {
      return selectedZoneId;
    }
    return zones[0]?.id ?? "";
  }, [selectedZoneId, zones]);
  const locationsQuery = useWarehouseLocationsQuery(
    shopId,
    branchId || undefined,
    activeZoneId || undefined,
  );
  const floorsQuery = useWarehouseLayoutFloorsQuery(
    shopId,
    branchId || undefined,
    warehouseLayoutOn && !!branchId,
  );
  const floors = floorsQuery.data?.items ?? [];
  const placementsQuery = useWarehouseLayoutPlacementsQuery(
    shopId,
    branchId || undefined,
    warehouseLayoutOn && !!branchId && activeTab === "list",
  );
  const zoneFloors = placementsQuery.data?.zones ?? {};
  const zoneNodeFloors = placementsQuery.data?.zoneNodes ?? {};
  const locationFloors = placementsQuery.data?.locations ?? {};

  useEffect(() => {
    if (!branchId) {
      setSelectedLayoutId(undefined);
      return;
    }
    if (floorsQuery.isLoading) return;
    if (floors.length === 0) {
      setSelectedLayoutId(undefined);
      return;
    }
    setSelectedLayoutId((prev) => {
      if (prev && floors.some((f) => f.id === prev)) return prev;
      if (search.layoutId && floors.some((f) => f.id === search.layoutId)) {
        return search.layoutId;
      }
      return floors[0]!.id;
    });
  }, [branchId, floors, floorsQuery.isLoading, search.layoutId]);

  const layoutQuery = useWarehouseLayoutQuery(
    shopId,
    branchId || undefined,
    selectedLayoutId,
    warehouseLayoutOn && activeTab === "layout",
  );

  const createZone = useCreateWarehouseZoneMutation(shopId);
  const updateZone = useUpdateWarehouseZoneMutation(shopId);
  const createLocation = useCreateWarehouseLocationMutation(shopId);
  const updateLocation = useUpdateWarehouseLocationMutation(shopId);
  const deleteZone = useDeleteWarehouseZoneMutation(shopId);
  const deleteLocation = useDeleteWarehouseLocationMutation(shopId);
  const createFloor = useCreateWarehouseLayoutFloorMutation(shopId);
  const deleteFloor = useDeleteWarehouseLayoutMutation(shopId);
  const assignZoneFloor = useAssignWarehouseZoneFloorMutation(shopId);

  const locations = locationsQuery.data?.items ?? [];

  const breadcrumbs = shopBreadcrumbs.inventoryLocations(shopId);

  async function handleCreateZone() {
    if (!branchId) return;
    try {
      const created = await createZone.mutateAsync({
        branchId,
        code: zoneCode.trim(),
        name: zoneName.trim(),
      });
      if (warehouseLayoutOn && zoneFloorLayoutId && created?.id) {
        await assignZoneFloor.mutateAsync({
          branchId,
          zoneId: created.id,
          layoutId: zoneFloorLayoutId,
          moveLocations: true,
        });
      }
      setZoneCode("MAIN");
      setZoneName("ໂຊນຫຼັກ");
      setZoneFloorLayoutId("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດເພີ່ມໂຊນໄດ້");
    }
  }

  function startEditZone(zone: { id: string; code: string; name: string }) {
    setEditingZoneId(zone.id);
    setEditZoneCode(zone.code);
    setEditZoneName(zone.name);
    setSelectedZoneId(zone.id);
  }

  function cancelEditZone() {
    setEditingZoneId(null);
    setEditZoneCode("");
    setEditZoneName("");
  }

  async function handleSaveZone(zoneId: string) {
    if (!branchId) return;
    const code = editZoneCode.trim();
    const name = editZoneName.trim();
    if (!code || !name) return;
    try {
      await updateZone.mutateAsync({
        zoneId,
        branchId,
        body: { code, name },
      });
      cancelEditZone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດບັນທຶກໂຊນໄດ້");
    }
  }

  async function handleCreateLocation() {
    if (!branchId || !activeZoneId || !locCode.trim()) return;
    try {
      await createLocation.mutateAsync({
        branchId,
        zoneId: activeZoneId,
        code: locCode.trim(),
        name: locName.trim() || null,
        isDefaultReceive: locations.length === 0,
      });
      setLocCode("");
      setLocName("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດເພີ່ມຕຳແໜ່ງໄດ້");
    }
  }

  function startEditLocation(loc: {
    id: string;
    code: string;
    name: string | null;
    isDefaultReceive: boolean;
  }) {
    setEditingLocationId(loc.id);
    setEditLocCode(loc.code);
    setEditLocName(loc.name ?? "");
    setEditLocIsDefaultReceive(loc.isDefaultReceive);
  }

  function cancelEditLocation() {
    setEditingLocationId(null);
    setEditLocCode("");
    setEditLocName("");
    setEditLocIsDefaultReceive(false);
  }

  async function handleSaveLocation(locationId: string) {
    if (!branchId || !activeZoneId) return;
    const code = editLocCode.trim();
    if (!code) return;
    try {
      await updateLocation.mutateAsync({
        locationId,
        branchId,
        zoneId: activeZoneId,
        body: {
          code,
          name: editLocName.trim() || null,
          isDefaultReceive: editLocIsDefaultReceive,
        },
      });
      cancelEditLocation();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດບັນທຶກຕຳແໜ່ງໄດ້");
    }
  }

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={breadcrumbs} />
      </Header>

      <Main className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 font-bold font-heading text-2xl">
              <MapPin className="h-6 w-6" />
              ຈັດການຕຳແໜ່ງສາງ
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ໂຊນ ແລະ ຊ່ອງເກັບສິນຄ້າ — ສະແກນ QR ຜ່ານລະຫັດສະແກນ
            </p>
          </div>
        </div>

        {warehouseLayoutOn ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={activeTab === "list" ? "default" : "outline"}
              size="sm"
              className="gap-1.5"
              onClick={() => setPageTab("list")}
            >
              <List className="size-4" aria-hidden />
              ລາຍການ
            </Button>
            <Button
              type="button"
              variant={activeTab === "layout" ? "default" : "outline"}
              size="sm"
              className="gap-1.5"
              onClick={() => setPageTab("layout")}
            >
              <LayoutGrid className="size-4" aria-hidden />
              ແຜນຜັງ
            </Button>
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">ເລືອກສາຂາ</CardTitle>
          </CardHeader>
          <CardContent>
            {branchesQuery.isLoading ? (
              <div className="flex justify-center py-6">
                <Loader />
              </div>
            ) : branches.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີສາຂາ — ເພີ່ມສາຂາຈາກໜ້າຕັ້ງຄ່າຮ້ານກ່ອນ
              </p>
            ) : (
              <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {branches.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      disabled={!b.isActive}
                      onClick={() => {
                        if (!b.isActive) return;
                        setBranchId(b.id);
                        setSelectedZoneId("");
                        cancelEditZone();
                      }}
                      className={cn(
                        "w-full rounded-lg border p-3 text-left transition-colors",
                        !b.isActive &&
                          "cursor-not-allowed opacity-50 hover:bg-transparent",
                        b.isActive &&
                          branchId === b.id &&
                          "border-primary bg-muted/40",
                        b.isActive &&
                          branchId !== b.id &&
                          "border-border hover:bg-muted/30",
                      )}
                    >
                      <p className="font-medium">{b.name}</p>
                      {!b.isActive ? (
                        <Badge variant="secondary" className="mt-1">
                          ປິດໃຊ້ງານ
                        </Badge>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {activeTab === "layout" ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">ແຜນຜັງສາງ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!branchId ? (
                <p className="text-muted-foreground text-sm">
                  ເລືອກສາຂາເພື່ອເບິດແຜນຜັງ.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    {floors.length === 0 ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled
                      >
                        ຊັ້ນ 1 (ອັດຕະໂນມັດ)
                      </Button>
                    ) : (
                      floors.map((f) => (
                        <Button
                          key={f.id}
                          type="button"
                          size="sm"
                          variant={
                            selectedLayoutId === f.id ? "default" : "outline"
                          }
                          onClick={() => setSelectedLayoutId(f.id)}
                        >
                          {f.name}
                          <span className="ml-1.5 font-mono text-[10px] opacity-70">
                            F{f.floorIndex + 1}
                            {f.nodeCount > 0 ? ` · ${f.nodeCount}` : ""}
                          </span>
                        </Button>
                      ))
                    )}
                    {canAdjust ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        disabled={createFloor.isPending || !branchId}
                        onClick={() => {
                          void createFloor
                            .mutateAsync({ branchId })
                            .then((row) => setSelectedLayoutId(row.id));
                        }}
                      >
                        <Plus className="size-3.5" />
                        ເພີ່ມຊັ້ນ
                      </Button>
                    ) : null}
                    {canAdjust && selectedLayoutId ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="gap-1 text-destructive"
                        disabled={deleteFloor.isPending}
                        onClick={() => {
                          void (async () => {
                            const ok = await confirm({
                              title: "ລຶບຊັ້ນອອກຈາກແຜນຜັງ?",
                              description:
                                "ຕຳແໜ່ງແລະຊ່ອງຍັງຄົງຢູ່ໃນລາຍການ — ພຽງຊັ້ນນີ້ຈະຖືກລຶບອອກຈາກແຜນຜັງເທົ່ານັ້ນ.",
                              actionText: "ລຶບຊັ້ນ",
                              ActionProps: { variant: "destructive" },
                            });
                            if (!ok) return;
                            await deleteFloor.mutateAsync({
                              branchId,
                              layoutId: selectedLayoutId,
                            });
                            setSelectedLayoutId(undefined);
                          })();
                        }}
                      >
                        <Trash2 className="size-3.5" />
                        ລຶບຊັ້ນ
                      </Button>
                    ) : null}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    ແຕ່ລະຊັ້ນມີແຜນຜັງຂອງຕົນເອງ. ຕຳແໜ່ງໜຶ່ງຢູ່ໄດ້ພຽງຊັ້ນດຽວ.
                    ປ່ຽນຊື່/ຈັດການຊັ້ນໃນແທັບລາຍການ.
                  </p>
                  {layoutQuery.isLoading || floorsQuery.isLoading ? (
                    <div className="flex justify-center py-12">
                      <Loader />
                    </div>
                  ) : layoutQuery.isError ? (
                    <p className="text-destructive text-sm">
                      ໂຫຼດແຜນຜັງລົ້ມເຫຼວ — ກວດສອບ feature flag warehouse_layout.
                    </p>
                  ) : layoutQuery.data && shopId ? (
                    <WarehouseLayoutEditor
                      shopId={shopId}
                      branchId={branchId}
                      data={layoutQuery.data}
                      canEdit={canAdjust}
                      focusLocationId={search.focusLocationId}
                      onLocationClick={(node) => {
                        if (!node.scanCode) return;
                        setScanModalInitial(node.scanCode);
                        setScanModalOpen(true);
                      }}
                    />
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>
        ) : null}

        {activeTab === "list" && branchId ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">ສະແກນຊ່ອງ</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap items-end gap-2">
              <div className="min-w-[200px] flex-1">
                <Label>ລະຫັດສະແກນ</Label>
                <Input
                  value={scanLookup}
                  onChange={(e) => setScanLookup(e.target.value)}
                  placeholder="WL-MAIN-..."
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  const code = scanLookup.trim();
                  setScanModalInitial(code || undefined);
                  setScanModalOpen(true);
                }}
              >
                ສະແກນ / ເບິ່ງໃນຊ່ອງ
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {activeTab === "list" && branchId && warehouseLayoutOn && shopId ? (
          <WarehouseLayoutFloorsPanel
            shopId={shopId}
            branchId={branchId}
            floors={floors}
            isLoading={floorsQuery.isLoading}
            canEdit={canAdjust}
            onOpenMap={(layoutId) => {
              setSelectedLayoutId(layoutId);
              setPageTab("layout");
            }}
          />
        ) : null}

        {activeTab === "list" && branchId ? (
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">ໂຊນ (Zones)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>ລະຫັດ</Label>
                    <Input
                      value={zoneCode}
                      onChange={(e) => setZoneCode(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>ຊື່</Label>
                    <Input
                      value={zoneName}
                      onChange={(e) => setZoneName(e.target.value)}
                    />
                  </div>
                </div>
                {warehouseLayoutOn ? (
                  <div>
                    <Label>ຊັ້ນ (ທາງເລືອກ)</Label>
                    <WarehouseZoneFloorSelect
                      floors={floors}
                      value={zoneFloorLayoutId}
                      onChange={(id) => setZoneFloorLayoutId(id ?? "")}
                    />
                  </div>
                ) : null}
                <RequireMerchantPermission
                  permission={MerchantPermissions.inventory.adjust}
                >
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void handleCreateZone()}
                    disabled={createZone.isPending || assignZoneFloor.isPending}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    ເພີ່ມໂຊນ
                  </Button>
                </RequireMerchantPermission>
                <ul className="space-y-2">
                  {zones.map((z) => (
                    <li
                      key={z.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (editingZoneId === z.id) return;
                        setSelectedZoneId(z.id);
                      }}
                      onKeyDown={(e) => {
                        if (editingZoneId === z.id) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedZoneId(z.id);
                        }
                      }}
                      className={cn(
                        "flex cursor-pointer items-center justify-between gap-2 rounded-lg border p-3 transition-colors",
                        activeZoneId === z.id
                          ? "border-primary bg-muted/40"
                          : "border-border hover:bg-muted/30",
                      )}
                    >
                      {editingZoneId === z.id ? (
                        <div
                          className="flex min-w-0 flex-1 flex-col gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="grid gap-2 sm:grid-cols-2">
                            <div>
                              <Label className="text-xs">ລະຫັດ</Label>
                              <Input
                                value={editZoneCode}
                                className="font-mono"
                                autoFocus
                                onChange={(e) =>
                                  setEditZoneCode(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    void handleSaveZone(z.id);
                                  }
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelEditZone();
                                  }
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">ຊື່</Label>
                              <Input
                                value={editZoneName}
                                onChange={(e) =>
                                  setEditZoneName(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    void handleSaveZone(z.id);
                                  }
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelEditZone();
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              size="sm"
                              disabled={
                                updateZone.isPending ||
                                !editZoneCode.trim() ||
                                !editZoneName.trim()
                              }
                              onClick={() => void handleSaveZone(z.id)}
                            >
                              ບັນທຶກ
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              disabled={updateZone.isPending}
                              onClick={cancelEditZone}
                            >
                              ຍົກເລີກ
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="min-w-0 flex-1 text-left">
                            <p className="font-medium">{z.name}</p>
                            <p className="font-mono text-muted-foreground text-xs">
                              {z.code}
                            </p>
                            {warehouseLayoutOn ? (
                              <div className="mt-1.5">
                                <WarehouseLayoutFloorBadges
                                  floors={zoneFloors[z.id]}
                                />
                              </div>
                            ) : null}
                          </div>
                          <div
                            className="flex flex-col items-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {warehouseLayoutOn && canAdjust ? (
                              <WarehouseZoneFloorSelect
                                className="h-8 max-w-[160px] rounded-md border border-input bg-background px-2 text-xs"
                                floors={floors}
                                value={
                                  zoneNodeFloors[z.id]?.[0]?.layoutId ?? ""
                                }
                                disabled={assignZoneFloor.isPending}
                                onChange={(id) => {
                                  void assignZoneFloor.mutateAsync({
                                    branchId,
                                    zoneId: z.id,
                                    layoutId: id,
                                    moveLocations: true,
                                  });
                                }}
                              />
                            ) : null}
                            <RequireMerchantPermission
                              permission={MerchantPermissions.inventory.adjust}
                            >
                              <div className="flex items-center gap-0.5">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditZone(z);
                                  }}
                                >
                                  ແກ້ໄຂ
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteZone.mutate(z.id, {
                                      onError: () =>
                                        toast.error("ລຶບໂຊນບໍ່ໄດ້ — ຍັງມີຕຳແໜ່ງຢູ່"),
                                    });
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </RequireMerchantPermission>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                  {zones.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      ຍັງບໍ່ມີໂຊນ — ເພີ່ມໂຊນທຳອິດ
                    </p>
                  ) : null}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">ຕຳແໜ່ງ (Locations)</CardTitle>
                <WarehouseLocationQrPrintButton
                  locations={locations}
                  shopName={currentShop?.name}
                />
              </CardHeader>
              <CardContent className="space-y-4">
                {activeZoneId ? (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label>ລະຫັດຊ່ອງ</Label>
                        <Input
                          value={locCode}
                          onChange={(e) => setLocCode(e.target.value)}
                          placeholder="A-01-03"
                        />
                      </div>
                      <div>
                        <Label>ຊື່ (ທາງເລືອກ)</Label>
                        <Input
                          value={locName}
                          onChange={(e) => setLocName(e.target.value)}
                        />
                      </div>
                    </div>
                    <RequireMerchantPermission
                      permission={MerchantPermissions.inventory.adjust}
                    >
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => void handleCreateLocation()}
                        disabled={createLocation.isPending}
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        ເພີ່ມຕຳແໜ່ງ
                      </Button>
                    </RequireMerchantPermission>
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm">ເລືອກໂຊນກ່ອນ</p>
                )}
                <ul className="space-y-2">
                  {locations.map((loc) => (
                    <li
                      key={loc.id}
                      className="flex items-start justify-between gap-2 rounded-lg border p-3"
                    >
                      {editingLocationId === loc.id ? (
                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                          <div className="grid gap-2 sm:grid-cols-2">
                            <div>
                              <Label className="text-xs">ລະຫັດຊ່ອງ</Label>
                              <Input
                                value={editLocCode}
                                className="font-mono"
                                autoFocus
                                onChange={(e) => setEditLocCode(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    void handleSaveLocation(loc.id);
                                  }
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelEditLocation();
                                  }
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">ຊື່ (ທາງເລືອກ)</Label>
                              <Input
                                value={editLocName}
                                onChange={(e) => setEditLocName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    void handleSaveLocation(loc.id);
                                  }
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelEditLocation();
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={editLocIsDefaultReceive}
                              onChange={(e) =>
                                setEditLocIsDefaultReceive(e.target.checked)
                              }
                            />
                            ຮັບເຂົ້າ default
                          </label>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              size="sm"
                              disabled={
                                updateLocation.isPending || !editLocCode.trim()
                              }
                              onClick={() => void handleSaveLocation(loc.id)}
                            >
                              ບັນທຶກ
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              disabled={updateLocation.isPending}
                              onClick={cancelEditLocation}
                            >
                              ຍົກເລີກ
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium font-mono">{loc.code}</p>
                            {loc.name ? (
                              <p className="text-muted-foreground text-sm">
                                {loc.name}
                              </p>
                            ) : null}
                            {loc.scanCode ? (
                              <p className="mt-1 font-mono text-xs">
                                QR: {loc.scanCode}
                              </p>
                            ) : null}
                            {loc.isDefaultReceive ? (
                              <Badge variant="secondary" className="mt-1">
                                ຮັບເຂົ້າ default
                              </Badge>
                            ) : null}
                            {warehouseLayoutOn ? (
                              <div className="mt-1.5">
                                <WarehouseLayoutFloorBadges
                                  floors={locationFloors[loc.id]}
                                />
                              </div>
                            ) : null}
                          </div>
                          <RequireMerchantPermission
                            permission={MerchantPermissions.inventory.adjust}
                          >
                            <div className="flex items-center gap-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => startEditLocation(loc)}
                              >
                                ແກ້ໄຂ
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  deleteLocation.mutate(loc.id, {
                                    onError: () =>
                                      toast.error("ລຶບບໍ່ໄດ້ — ຍັງມີສິນຄ້າໃນຊ່ອງ"),
                                  })
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </RequireMerchantPermission>
                        </>
                      )}
                    </li>
                  ))}
                  {activeZoneId && locations.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      ຍັງບໍ່ມີຕຳແໜ່ງໃນໂຊນນີ້
                    </p>
                  ) : null}
                </ul>
              </CardContent>
            </Card>
          </div>
        ) : null}

        {shopId ? (
          <WarehouseLocationScanModal
            shopId={shopId}
            branchId={branchId || undefined}
            open={scanModalOpen}
            onOpenChange={setScanModalOpen}
            canAdjust={canAdjust}
            initialScanCode={scanModalInitial}
          />
        ) : null}
      </Main>
    </RequireMerchantPermission>
  );
}
